import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { usePipeline } from '../../context/PipelineContext';
import { PIPELINE_STAGES, LEAD_SOURCES, SERVICE_TIERS, calculateAnnualizedValue, daysBetween, getEffectiveProbability } from '../../utils/pipelineCalculations';
import { LOSS_REASONS_LOST, LOSS_REASONS_UNQUALIFIED } from '../../utils/sfPicklists';
import CloseReasonModal from './CloseReasonModal';
import { apiFetch } from '../../utils/apiFetch';

// Get division from prospect (stored field or derived from service tier)
function getDivision(prospect) {
  if (prospect.division) return prospect.division;
  const tier = SERVICE_TIERS[prospect.serviceTier];
  return tier?.division || '';
}

// Get service tier display label
function getTierLabel(tierKey) {
  return SERVICE_TIERS[tierKey]?.label || tierKey || '--';
}
import { formatCurrency } from '../../utils/formatting';

// Single dropdown that hosts every per-prospect action.
//
// IMPORTANT: the menu portals to document.body so it escapes any parent
// container with overflow-hidden / overflow-x-auto (the table card has
// overflow-x-auto which was clipping the dropdown). Position is computed
// from the trigger's bounding rect and uses fixed positioning so it
// stays anchored on scroll-within-card without going off-screen.
function ActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Compute menu position based on the trigger's rect. Right-align to
  // the trigger so menus near the right edge stay on-screen.
  function updatePosition() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 200; // matches min-w-[200px] below
    const menuMaxHeight = 380;
    // Default: below the trigger, right-aligned
    let top = rect.bottom + 4;
    let left = rect.right - menuWidth;
    // If the menu would overflow the bottom, flip above the trigger
    if (top + menuMaxHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuMaxHeight - 4);
    }
    // Don't let the menu fall off the left edge
    if (left < 8) left = 8;
    setPos({ top, left });
  }

  useEffect(() => {
    if (!open) return;
    updatePosition();
    function handleClick(e) {
      // Click outside closes. Portal node lives outside triggerRef, so
      // check both the trigger and the menu.
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e) { if (e.key === 'Escape') setOpen(false); }
    function handleResize() { updatePosition(); }
    function handleScroll() { setOpen(false); } // close on scroll — re-anchoring is jumpy
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // capture to catch scrolls in scroll containers
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const menu = open ? (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: '200px', maxWidth: '260px' }}
      className="z-50 bg-white border border-gray-200 rounded-[4px] shadow-lg py-1"
    >
      {items.map((item, i) => {
        if (item.divider) {
          return <div key={`d-${i}`} className="my-1 border-t border-gray-100" />;
        }
        const baseClass = `w-full text-left px-3 py-1.5 text-[12px] hover:bg-bone/60 flex items-center gap-2 ${item.className || 'text-navy'}`;
        if (item.to) {
          return (
            <Link key={item.label} to={item.to} className={baseClass} onClick={() => setOpen(false)}>
              {item.label}
            </Link>
          );
        }
        return (
          <button
            key={item.label}
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); item.onClick?.(); }}
            className={baseClass}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="px-2 py-1 text-[11px] font-bold text-navy hover:bg-navy/10 rounded-[3px] border border-navy/20"
        title="Actions"
      >
        Actions
      </button>
      {menu && createPortal(menu, document.body)}
    </>
  );
}

// Division badge colors
const DIVISION_COLORS = {
  bwm: 'bg-bullogic-blue/15 text-bullogic-blue',
  bts: 'bg-deep-teal/15 text-deep-teal',
  bia: 'bg-sky-blue/15 text-sky-blue',
};

const DIVISION_LABELS = {
  bwm: 'BWM',
  bts: 'BTS',
  bia: 'BIA',
};

// Stage badge opacity based on probability (darker = closer to close)
function stageBadgeClass(stageId) {
  const stage = PIPELINE_STAGES.find(s => s.id === stageId);
  if (!stage) return 'bg-gray-100 text-gray-600';
  if (stageId === 'closed-won') return 'bg-deep-teal/20 text-deep-teal';
  if (stageId === 'closed-lost') return 'bg-rose/20 text-rose';
  // Active stages: opacity from 10% to 80% based on probability
  const opacity = Math.round(stage.probability * 100);
  return `bg-navy/${Math.max(opacity, 10)} text-navy`;
}

function getStageOrder(stageId) {
  const stage = PIPELINE_STAGES.find(s => s.id === stageId);
  return stage ? stage.order : 0;
}

function getStagLabel(stageId) {
  const stage = PIPELINE_STAGES.find(s => s.id === stageId);
  return stage ? stage.shortLabel : stageId;
}

// Fixed column widths so tables align across division sections
const SORTABLE_COLUMNS = [
  { key: 'name', label: 'Name', width: '18%' },
  { key: 'division', label: 'Division', width: '7%' },
  { key: 'serviceTier', label: 'Service Tier', width: '12%' },
  { key: 'stage', label: 'Stage', width: '9%' },
  { key: 'probability', label: 'Prob.', width: '6%' },
  { key: 'value', label: 'Est. Annual', width: '10%' },
  { key: 'weighted', label: 'Weighted', width: '10%' },
  { key: 'days', label: 'Days', width: '5%' },
  { key: 'expectedClose', label: 'Expected Close', width: '9%' },
  { key: 'source', label: 'Source', width: '9%' },
];
const ACTIONS_WIDTH = '5%';

export default function ProspectTable({ prospects, onEdit, onAdvance, onPushToPortal, onViewPending, divisionFilter, stageFilter }) {
  const { advanceStage, closeAsLost, markUnqualified, deleteProspect, reopenProspect, updateProspect, sourceStats } = usePipeline();
  const updateProspectFn = updateProspect;
  // Use the onAdvance prop if provided (intercepts Won), otherwise default to advanceStage
  const handleAdvance = onAdvance || advanceStage;
  const [sortKey, setSortKey] = useState('stage');
  const [sortAsc, setSortAsc] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  // Set of prospect IDs currently enrolled in an AC follow-up sequence.
  // Populated by a single bulk fetch; used to paint a ✉ indicator on
  // enrolled rows. Refreshes on mount + every 60s in case Adam starts a
  // sequence from another tab.
  const [enrolledIds, setEnrolledIds] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiFetch('/api/data/email-followup/active');
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setEnrolledIds(new Set((j.enrolled || []).map(e => e.prospectId)));
      } catch { /* tolerate — indicator just won't show */ }
    }
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  // Tracks which prospect has the close-reason picker open + which mode.
  // mode: 'lost' | 'unqualified'
  const [closeReasonModal, setCloseReasonModal] = useState(null);
  // Group by division when no specific division is filtered
  const [groupByDivision, setGroupByDivision] = useState(true);

  // Filter prospects
  let filtered = prospects || [];
  if (divisionFilter) {
    filtered = filtered.filter(p => getDivision(p) === divisionFilter);
  }
  if (stageFilter) {
    filtered = filtered.filter(p => p.stage === stageFilter);
  }

  // Separate active vs terminal. Unqualified is terminal too — moves into
  // the collapsed Closed section with a Reopen action so disqualified
  // prospects can be reactivated when they re-engage.
  const active = filtered.filter(
    p => p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified'
  );
  const closedWon = filtered.filter(p => p.stage === 'closed-won');
  const closedLost = filtered.filter(p => p.stage === 'closed-lost');
  const unqualified = filtered.filter(p => p.stage === 'unqualified');

  // Sort function
  function sortProspects(list) {
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'division':
          cmp = getDivision(a).localeCompare(getDivision(b));
          break;
        case 'serviceTier':
          cmp = (a.serviceTier || '').localeCompare(b.serviceTier || '');
          break;
        case 'stage':
          cmp = getStageOrder(b.stage) - getStageOrder(a.stage); // furthest along first
          if (cmp === 0) cmp = calculateAnnualizedValue(b.estimatedValue) - calculateAnnualizedValue(a.estimatedValue);
          break;
        case 'value':
          cmp = calculateAnnualizedValue(a.estimatedValue) - calculateAnnualizedValue(b.estimatedValue);
          break;
        case 'probability': {
          const probA = getEffectiveProbability(a, sourceStats).probability;
          const probB = getEffectiveProbability(b, sourceStats).probability;
          cmp = probA - probB;
          break;
        }
        case 'weighted': {
          const probA = getEffectiveProbability(a, sourceStats).probability;
          const probB = getEffectiveProbability(b, sourceStats).probability;
          cmp = (calculateAnnualizedValue(a.estimatedValue) * probA) - (calculateAnnualizedValue(b.estimatedValue) * probB);
          break;
        }
        case 'days':
          cmp = daysBetween(a.createdDate) - daysBetween(b.createdDate);
          break;
        case 'expectedClose':
          cmp = (a.expectedCloseDate || '').localeCompare(b.expectedCloseDate || '');
          break;
        case 'source':
          cmp = (a.source || '').localeCompare(b.source || '');
          break;
        default:
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      // Default descending for stage and value, ascending for others
      setSortAsc(key !== 'stage' && key !== 'value');
    }
  }

  function handleCloseLost(prospect) {
    setCloseReasonModal({ prospect, mode: 'lost' });
  }

  function handleMarkUnqualified(prospect) {
    setCloseReasonModal({ prospect, mode: 'unqualified' });
  }

  function handleReasonConfirmed(reason) {
    if (!closeReasonModal) return;
    const { prospect, mode } = closeReasonModal;
    if (mode === 'lost') {
      closeAsLost(prospect.id, reason);
    } else {
      markUnqualified(prospect.id, reason);
    }
    setCloseReasonModal(null);
  }

  // Override the smart probability with a manual value, or clear the override
  function handleProbabilityOverride(p) {
    const current = (typeof p.manualProbability === 'number' ? p.manualProbability : null);
    const promptText = current !== null
      ? `Override is ${(current * 100).toFixed(0)}%. Enter new value (0-100), leave blank to clear:`
      : `Set manual probability override for "${p.name}" (0-100):`;
    const input = window.prompt(promptText, current !== null ? (current * 100).toFixed(0) : '');
    if (input === null) return;
    if (input.trim() === '') {
      updateProspectFn(p.id, { manualProbability: null });
      return;
    }
    const num = parseFloat(input);
    if (isNaN(num) || num < 0 || num > 100) {
      window.alert('Please enter a number between 0 and 100');
      return;
    }
    updateProspectFn(p.id, { manualProbability: num / 100 });
  }

  const sortedActive = sortProspects(active);
  const sortedWon = sortProspects(closedWon);
  const sortedLost = sortProspects(closedLost);
  const sortedUnqual = sortProspects(unqualified);

  const thClass = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.08em] text-white cursor-pointer select-none hover:bg-navy/80';
  const tdClass = 'px-3 py-2 text-[12px] text-navy';

  function renderRow(p, isClosed = false) {
    const annualValue = calculateAnnualizedValue(p.estimatedValue);
    const { probability, isManual, base } = getEffectiveProbability(p, sourceStats);
    const weightedValue = annualValue * probability;
    const days = p.createdDate ? daysBetween(p.createdDate, p.closedDate || null) : null;
    const isDecayed = !isManual && probability < base - 0.01; // smart decay applied
    const isWon = p.stage === 'closed-won';
    const isLost = p.stage === 'closed-lost';

    let rowBg = '';
    if (isWon) rowBg = 'bg-deep-teal/5';
    if (isLost) rowBg = 'bg-rose/5';

    const isEnrolled = enrolledIds.has(p.id);
    const pushed = !!p.portalHouseholdId;
    return (
      <tr key={p.id} className={`border-b border-gray-100 hover:bg-bone/50 ${rowBg}`}>
        <td className={tdClass}>
          <span className="font-bold">{p.name}</span>
          {p.company && <span className="text-[11px] text-[#888] ml-1">({p.company})</span>}
          {isEnrolled && (
            <Link
              to={`/crm/prospects/${p.id}/follow-up`}
              title="Currently enrolled in an AC email follow-up sequence — click to manage"
              className="ml-1.5 text-deep-teal hover:text-deep-teal/80 align-middle"
            >
              ✉
            </Link>
          )}
        </td>
        <td className={tdClass}>
          {(() => { const div = getDivision(p); return (
            <span className={`inline-block px-2 py-0.5 rounded-[3px] text-[9px] font-bold uppercase tracking-wider ${DIVISION_COLORS[div] || 'bg-gray-100 text-gray-600'}`}>
              {DIVISION_LABELS[div] || div || '--'}
            </span>
          ); })()}
        </td>
        <td className={`${tdClass} text-[11px]`}>{getTierLabel(p.serviceTier)}</td>
        <td className={tdClass}>
          <span className={`inline-block px-2 py-0.5 rounded-[3px] text-[9px] font-bold uppercase tracking-wider ${stageBadgeClass(p.stage)}`}>
            {getStagLabel(p.stage)}
          </span>
        </td>
        <td className={`${tdClass} text-center`}>
          <button
            onClick={(e) => { e.stopPropagation(); handleProbabilityOverride(p); }}
            title={isManual
              ? `Manually set to ${(probability * 100).toFixed(0)}%. Stage default: ${(base * 100).toFixed(0)}%. Click to clear or change.`
              : isDecayed
                ? `Smart probability (decayed from ${(base * 100).toFixed(0)}% due to time in stage). Click to override.`
                : `Stage default. Click to override.`}
            className={`text-[11px] font-bold px-1.5 py-0.5 rounded-[3px] cursor-pointer hover:opacity-80 ${
              isManual
                ? 'bg-rose/15 text-rose'
                : isDecayed
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-[#666] hover:bg-gray-100'
            }`}
          >
            {(probability * 100).toFixed(0)}%
          </button>
        </td>
        <td className={`${tdClass} text-right font-bold`}>{formatCurrency(annualValue)}</td>
        <td className={`${tdClass} text-right font-bold ${isManual ? 'text-rose' : 'text-deep-teal'}`}>{formatCurrency(weightedValue)}</td>
        <td className={`${tdClass} text-center`}>{days != null ? days : '--'}</td>
        <td className={`${tdClass} text-[11px]`}>{p.expectedCloseDate || '--'}</td>
        <td className={`${tdClass} text-[11px]`}>{LEAD_SOURCES.find(s => s.id === p.source)?.label || p.source || '--'}</td>
        <td className={`${tdClass} text-center`}>
          {!isClosed ? (
            <ActionsMenu
              items={[
                { label: '\u25B6  Advance stage', onClick: () => handleAdvance(p.id), className: 'text-deep-teal font-bold' },
                { label: '\u270E  Edit prospect', onClick: () => onEdit && onEdit(p), className: 'text-bullogic-blue' },
                { divider: true },
                { label: 'D  Discovery call notes', to: `/crm/prospects/${p.id}/discovery` },
                { label: 'S  Strategy meeting notes', to: `/crm/prospects/${p.id}/strategy` },
                { label: 'F  Email follow-up', to: `/crm/prospects/${p.id}/follow-up` },
                { divider: true },
                pushed
                  ? { label: '✓  Pushed to portal', onClick: () => onViewPending && onViewPending(), className: 'text-deep-teal font-bold' }
                  : { label: 'Push to portal', onClick: () => onPushToPortal && onPushToPortal(p), className: 'text-deep-teal font-bold' },
                { divider: true },
                { label: 'Mark unqualified', onClick: () => handleMarkUnqualified(p), className: 'text-soft-coral font-bold' },
                { label: '\u2715  Close as lost', onClick: () => handleCloseLost(p), className: 'text-rose font-bold' },
                { divider: true },
                { label: '\u{1F5D1}  Delete (test data only)', onClick: () => {
                  if (window.confirm(`Permanently delete "${p.name}"? This removes the prospect from BCC entirely. Use only for test data \u2014 for real prospects use Mark Unqualified or Close as Lost.`)) {
                    deleteProspect(p.id);
                  }
                }, className: 'text-rose' },
              ]}
            />
          ) : (
            <ActionsMenu
              items={[
                { label: 'Reopen prospect', onClick: () => reopenProspect(p.id), className: 'text-bullogic-blue font-bold' },
                { divider: true },
                { label: '\u{1F5D1}  Delete (test data only)', onClick: () => {
                  if (window.confirm(`Permanently delete "${p.name}"? This removes the prospect from BCC entirely. Use only for test data.`)) {
                    deleteProspect(p.id);
                  }
                }, className: 'text-rose' },
              ]}
            />
          )}
        </td>
      </tr>
    );
  }

  if ((!prospects || prospects.length === 0) && !divisionFilter && !stageFilter) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-8 text-center">
        <div className="text-[14px] text-[#888]">No prospects yet</div>
        <div className="text-[12px] text-[#aaa] mt-1">Add your first prospect to get started.</div>
      </div>
    );
  }

  // Group toggle is only useful when not already filtered to one division
  const showGroupToggle = !divisionFilter;

  const renderColgroup = () => (
    <colgroup>
      {SORTABLE_COLUMNS.map(col => <col key={col.key} style={{ width: col.width }} />)}
      <col style={{ width: ACTIONS_WIDTH }} />
    </colgroup>
  );

  const renderTableHeader = () => (
    <thead>
      <tr className="bg-navy">
        {SORTABLE_COLUMNS.map(col => (
          <th
            key={col.key}
            onClick={() => handleSort(col.key)}
            className={thClass}
            style={{ width: col.width }}
          >
            {col.label}
            {sortKey === col.key && (
              <span className="ml-1 text-[8px]">{sortAsc ? '\u25B2' : '\u25BC'}</span>
            )}
          </th>
        ))}
        <th className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.08em] text-white" style={{ width: ACTIONS_WIDTH }}>
          Actions
        </th>
      </tr>
    </thead>
  );

  // Group prospects by division, return ordered list of [divisionKey, prospects]
  const groupProspectsByDivision = (list) => {
    const groups = { bwm: [], bts: [], bia: [], unknown: [] };
    for (const p of list) {
      const d = getDivision(p);
      if (groups[d]) groups[d].push(p);
      else groups.unknown.push(p);
    }
    const order = ['bwm', 'bts', 'bia', 'unknown'];
    return order.filter(k => groups[k].length > 0).map(k => [k, groups[k]]);
  };

  // Render one division as a fully separate card with its own header + table
  const renderDivisionCard = (div, list) => (
    <div key={div} className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
      <div className={`px-4 py-3 flex items-center justify-between ${DIVISION_COLORS[div] || 'bg-gray-100 text-gray-600'}`}>
        <div className="flex items-center gap-3">
          <span className="text-[14px] font-bold uppercase tracking-[0.1em]">{DIVISION_LABELS[div] || div}</span>
          <span className="text-[11px] opacity-70">{list.length} active prospect{list.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-4 text-[11px] font-bold opacity-80">
          <span>
            {formatCurrency(list.reduce((s, p) => s + calculateAnnualizedValue(p.estimatedValue), 0))} total
          </span>
          <span>
            {formatCurrency(list.reduce((s, p) => {
              // Use effective probability (manual override or smart-decayed) so this matches the rows
              const { probability } = getEffectiveProbability(p, sourceStats);
              return s + calculateAnnualizedValue(p.estimatedValue) * probability;
            }, 0))} weighted
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          {renderColgroup()}{renderTableHeader()}
          <tbody>{list.map(p => renderRow(p, false))}</tbody>
        </table>
      </div>
    </div>
  );

  // Build separate sections per division
  const divisionGroups = groupProspectsByDivision(sortedActive);

  return (
    <div className="space-y-4">
      {/* View toggle (only when not pre-filtered to single division) */}
      {showGroupToggle && (
        <div className="flex items-center justify-end gap-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#888]">View:</span>
          <div className="flex items-center bg-white border border-gray-200 rounded-[3px] p-0.5 shadow-sm">
            <button
              onClick={() => setGroupByDivision(true)}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-[2px] transition-colors ${
                groupByDivision ? 'bg-navy text-white' : 'text-[#888] hover:text-navy'
              }`}
            >
              Separate Divisions
            </button>
            <button
              onClick={() => setGroupByDivision(false)}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-[2px] transition-colors ${
                !groupByDivision ? 'bg-navy text-white' : 'text-[#888] hover:text-navy'
              }`}
            >
              All Combined
            </button>
          </div>
        </div>
      )}

      {/* Separate division cards */}
      {showGroupToggle && groupByDivision && sortedActive.length > 0 ? (
        divisionGroups.map(([div, list]) => renderDivisionCard(div, list))
      ) : (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              {renderColgroup()}{renderTableHeader()}
              <tbody>
                {sortedActive.length > 0
                  ? sortedActive.map(p => renderRow(p, false))
                  : (
                    <tr>
                      <td colSpan={11} className="px-3 py-4 text-center text-[12px] text-[#888]">
                        No active prospects match filters
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Closed section (collapsible, single card across all divisions) */}
      {(closedWon.length > 0 || closedLost.length > 0 || unqualified.length > 0) && (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
          <button
            onClick={() => setShowClosed(!showClosed)}
            className="w-full px-4 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-[#888] hover:bg-bone/50 flex items-center gap-2"
          >
            <span className="text-[8px]">{showClosed ? '\u25BC' : '\u25B6'}</span>
            Closed ({closedWon.length} Won, {closedLost.length} Lost, {unqualified.length} Unqualified)
          </button>
          {showClosed && (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                {renderColgroup()}{renderTableHeader()}
                <tbody>
                  {sortedWon.map(p => renderRow(p, true))}
                  {sortedLost.map(p => renderRow(p, true))}
                  {sortedUnqual.map(p => renderRow(p, true))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Loss / unqualified reason picker. Required because Loss_Reason__c
          in Salesforce is a picklist — freeform text rejects. */}
      {closeReasonModal && (
        <CloseReasonModal
          title={closeReasonModal.mode === 'lost' ? 'Close as Lost' : 'Mark Unqualified'}
          prospectName={closeReasonModal.prospect?.name}
          options={closeReasonModal.mode === 'lost' ? LOSS_REASONS_LOST : LOSS_REASONS_UNQUALIFIED}
          confirmLabel={closeReasonModal.mode === 'lost' ? 'Close Lost' : 'Mark Unqualified'}
          onConfirm={handleReasonConfirmed}
          onCancel={() => setCloseReasonModal(null)}
        />
      )}
    </div>
  );
}
